import { Icon } from '@eventbrite/eds-icon';
import { ChevronDownChunky } from '@eventbrite/eds-iconography';
import { GenericLazyString } from '@eventbrite/i18n';
import { $FixMe } from '@eventbrite/ts-utils';
import { History } from 'history';
import debounce from 'lodash/debounce';
import React from 'react';
import { LocationAutocompleteContainer } from '../../../../containers/LocationAutocompleteContainer';
import './cityBrowseHeader.scss';
import { CityBrowseHeaderImage } from './CityBrowseHeaderImage';

const ChevronIcon = (
    <Icon color="ui-blue--hover" size="medium" type={<ChevronDownChunky />} />
);

interface CityBlurbProps {
    cityBlurb?: GenericLazyString | string | Element;
}
const CityBlurb = ({ cityBlurb }: CityBlurbProps) => {
    let component = null;

    if (cityBlurb) {
        component = (
            // using dangerouslySetInnerHtml because data from wagtail is written in HTML
            <div
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: cityBlurb.toString() }}
                className="cityBlurb eds-text-bm eds-l-mar-vert-4 spaced-paragraphs"
                data-spec="city-blurb"
            />
        );
    }

    return component;
};

const isContentLongerThanInput = (
    proxyWidthElement: React.RefObject<any>,
    inputContainerElement: React.RefObject<any>,
) => {
    const characterLength = 20;

    const proxyWidth =
        proxyWidthElement?.current?.getBoundingClientRect().width;
    const containerWidth =
        inputContainerElement?.current?.getBoundingClientRect().width;
    if (typeof proxyWidth !== 'number' || typeof containerWidth !== 'number') {
        return;
    }
    return proxyWidth > containerWidth - characterLength;
};

export const useIsMultilineInput = () => {
    const [isMultiline, setIsMultiline] = React.useState(false);
    const proxyRef = React.useRef(null);
    const inputContainerRef = React.useRef(null);

    const debouncedListener = React.useCallback(() => {
        const checkNodes = () => {
            const shouldBeMultiline = isContentLongerThanInput(
                proxyRef,
                inputContainerRef,
            );

            if (shouldBeMultiline && !isMultiline) {
                setIsMultiline(true);
            } else if (!shouldBeMultiline && isMultiline) {
                setIsMultiline(false);
            }
        };

        const debouncedCheckNodes = debounce(checkNodes, 150);

        debouncedCheckNodes();
    }, [isMultiline]);

    React.useEffect(() => {
        isContentLongerThanInput(proxyRef, inputContainerRef) &&
            setIsMultiline(true);

        window.addEventListener('resize', debouncedListener);

        () => window.removeEventListener('resize', debouncedListener);
    }, [debouncedListener]);

    return { isMultiline, proxyRef, inputContainerRef };
};

interface CityBrowseHeaderProps {
    currentPlace?: string;
    cityBlurb?: GenericLazyString | string | Element;
    cityImage?: string;
    domain: string;
    experiments?: any;
    isBotRequest?: boolean;
    handleUseCurrentLocation?: Function;
    handleLocationSelect?: Function;
    handleLocationSuggestionChange?: Function;
    h1?: string;
    h1Prefix?: string;
    history?: History;
    isOnline?: boolean;
    isUseCurrentLocationEnabled?: boolean;
    isWaitingForLocation?: boolean;
    /*
     * Array of location suggestions for consumption by
     * the location switcher
     */
    locationSuggestions?: {
        suggestions?: $FixMe;
        hasSuggestions?: boolean;
    };
    onGoogleMapsLoaded?: Function;
    shouldHideMask?: boolean;
    /*
     * Tracking event from state from @eventbrite/site-analytics
     */
    trackGAEvent: Function;
}

export const CityBrowseHeader = (props: CityBrowseHeaderProps) => {
    const { isMultiline, proxyRef, inputContainerRef } = useIsMultilineInput();

    const {
        cityBlurb,
        cityImage,
        shouldHideMask,
        experiments,
        currentPlace,
        h1Prefix,
        handleUseCurrentLocation,
        handleLocationSelect,
        history,
        isBotRequest,
    } = props;

    return (
        <header className="city-browse-header">
            <div className="city-browse-header__main eds-g-cell eds-g-cell-1-1 eds-g-cell-mn-4-12">
                <div className="city-browse-header__aside city-browse-header__aside--small eds-g-cell eds-g-cell-1-1">
                    <CityBrowseHeaderImage
                        currentPlace={currentPlace}
                        cityImage={cityImage}
                        shouldHideMask={shouldHideMask}
                        experiments={experiments}
                    />
                </div>

                <div className="city-browse-header__main__content eds-align--center-vertical">
                    <div className="eds-g-cell eds-g-cell-1-1 eds-g-offset-mn-1-12 eds-g-cell--has-overflow eds-l-pad-hor-4">
                        <h1>
                            <span className="eds-text-bm eds-l-mar-bot-4">
                                {h1Prefix}{' '}
                            </span>
                            <span
                                ref={inputContainerRef}
                                className="city-browse-header__city-name-text"
                                data-current-text={currentPlace}
                                data-testid="city-browse-header"
                                data-spec="location-autocomplete"
                            >
                                {isBotRequest ? (
                                    currentPlace
                                ) : (
                                    <LocationAutocompleteContainer
                                        handleUseCurrentLocation={
                                            handleUseCurrentLocation
                                        }
                                        handleLocationSelect={
                                            handleLocationSelect
                                        }
                                        prefix={ChevronIcon}
                                        history={history}
                                        autocompleteProps={{
                                            borderType: 'content',
                                            preventInputValuePrefill: true,
                                            id: 'browse-location-selector',
                                            isMultiline,
                                        }}
                                    />
                                )}
                            </span>
                        </h1>
                        <div
                            className="city-browse-header__city-name-text--width-proxy"
                            ref={proxyRef}
                            style={{
                                position: 'absolute',
                                color: 'transparent',
                                pointerEvents: 'none',
                            }}
                        >
                            {currentPlace}
                        </div>
                        <CityBlurb cityBlurb={cityBlurb} />
                    </div>
                </div>
            </div>

            <aside className="city-browse-header__aside city-browse-header__aside--large eds-g-cell eds-g-cell-7-12 eds-g-offset-1-12 eds-l-mn-pad-left-2">
                <CityBrowseHeaderImage
                    currentPlace={currentPlace}
                    cityImage={cityImage}
                    shouldHideMask={shouldHideMask}
                    experiments={experiments}
                />
            </aside>
        </header>
    );
};
